/**
 * Which days Australia Post takes a lodgement, by mail type.
 *
 * Addressed bulk mail (PreSort, Regular or Priority) lodges on any business
 * day. Unaddressed Mail (Standard) lodges five business days before the
 * Monday of its delivery week, so on a Monday, and Australia Post does not
 * deliver it from the second week of December until January.
 *
 * Lodgement dates are stored as UTC midnight, so all day maths is UTC.
 *
 * ponytail: national public holidays only. State holidays (Melbourne Cup,
 * King's Birthday dates, Easter Tuesday in Tasmania) and the AusPost rule that
 * moves an unaddressed lodgement to the Friday before a holiday week are not
 * modelled; those still go through the "call eConnect" path. Upgrade path is
 * a per-state holiday table keyed on campaign.state.
 */

const DAY_MS = 86_400_000;

const iso = (d: Date) => d.toISOString().slice(0, 10);
const utc = (y: number, m: number, d: number) => new Date(Date.UTC(y, m, d));
const isWeekend = (d: Date) => d.getUTCDay() === 0 || d.getUTCDay() === 6;

/** Gregorian Easter Sunday (anonymous algorithm). */
function easterSunday(y: number): Date {
  const a = y % 19;
  const b = Math.floor(y / 100);
  const c = y % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const n = h + l - 7 * m + 114;
  return utc(y, Math.floor(n / 31) - 1, (n % 31) + 1);
}

const cache = new Map<number, Set<string>>();

/** National public holidays as `YYYY-MM-DD`, with weekday substitutes. */
export function nationalPublicHolidays(year: number): Set<string> {
  const hit = cache.get(year);
  if (hit) return hit;
  const easter = easterSunday(year);
  const out = new Set<string>([
    iso(new Date(easter.getTime() - 2 * DAY_MS)),
    iso(new Date(easter.getTime() + DAY_MS)),
    iso(utc(year, 3, 25)),
  ]);
  // A fixed holiday on a weekend moves to the next weekday that is not already one.
  const substituted = [utc(year, 0, 1), utc(year, 0, 26), utc(year, 11, 25), utc(year, 11, 26)];
  for (const d of substituted) out.add(iso(d));
  for (const d of substituted) {
    if (!isWeekend(d)) continue;
    let next = new Date(d.getTime() + DAY_MS);
    while (isWeekend(next) || out.has(iso(next))) next = new Date(next.getTime() + DAY_MS);
    out.add(iso(next));
  }
  cache.set(year, out);
  return out;
}

export function isPublicHoliday(epoch: number): boolean {
  const d = new Date(epoch * 1000);
  return nationalPublicHolidays(d.getUTCFullYear()).has(iso(d));
}

/** Why Australia Post will not take this mail type on this day, or null when it will. */
export function lodgementDayBlocker(epoch: number, mailType: string): string | null {
  const d = new Date(epoch * 1000);
  if (mailType === "unaddressed") {
    if (d.getUTCDay() !== 1) {
      return "Unaddressed mail is lodged on a Monday, the week before it is delivered. Choose a Monday.";
    }
    if (isPublicHoliday(epoch)) {
      return "That Monday is a public holiday, so Australia Post will not take the lodgement. Choose another Monday.";
    }
    const delivery = new Date(d.getTime() + 7 * DAY_MS);
    if (delivery.getUTCMonth() === 11 && delivery.getUTCDate() >= 8) {
      return "Australia Post does not deliver unaddressed mail from the second week of December until January. Choose an earlier or later Monday.";
    }
    return null;
  }
  if (isWeekend(d)) {
    return "Australia Post does not take addressed mail lodgements on weekends. Choose a weekday.";
  }
  if (isPublicHoliday(epoch)) {
    return "That day is a public holiday, so Australia Post will not take the lodgement. Choose another weekday.";
  }
  return null;
}

/** One line for the picker saying which days are open. */
export function lodgementDayHint(mailType: string): string {
  return mailType === "unaddressed"
    ? "Unaddressed mail lodges on Mondays, not public holidays or mid-December."
    : "Addressed mail lodges on weekdays, not public holidays.";
}
